import _ from 'lodash';
import { batchActions } from 'redux-batched-actions';

import * as Sentry from '@sentry/browser';

import * as Account from 'app/entities/Account';
import * as Profile from 'app/entities/Profile';
import ActionTypes from 'app/account/ActionTypes';
import Data from 'app/account/Data';
import TrackData from 'app/track/Data';
import z from 'app/common/z';
import { legacyAccountAndProfileCombo } from 'app/account/Selectors';

const recordUserIdOnce = _.once(TrackData.recordUserId);

// Eventually, we'll remove this code. Just a few remaining
// places reading from the old account model.
// Make sure the account model exists, since technically there's a
// race condition with legacy/app.js to create it.
const createLegacyBackboneModel = data => {
  if (_.isFunction(_.get(z, ['model', 'Account']))) {
    z.app.account = z.app.account || new z.model.Account();
    z.app.account.set(data);
  }
};

const setUserContext = profile => {
  Sentry.configureScope(scope => {
    scope.setUser({
      email: Profile.selectors.email(profile),
      id: Profile.selectors.id(profile),
    });
  });
};

const actions = {
  resetUserData() {
    return batchActions([
      Account.actions.resetCollection(),
      Profile.actions.resetCollection(),
    ]);
  },

  reloadAccount() {
    return actions.loadAccount(true);
  },

  loadAccount(shouldReload = false) {
    return (dispatch, getState) => {
      const actionCreators = shouldReload
        ? [
            Account.actions.forceLoadCollection,
            Profile.actions.forceLoadCollection,
          ]
        : [Account.actions.loadCollection, Profile.actions.loadCollection];
      return Promise.all(
        actionCreators.map(actionCreator => dispatch(actionCreator()))
      ).then(() => {
        const currentProfile = Profile.selectors.all.currentProfile(getState());
        if (currentProfile) {
          createLegacyBackboneModel(legacyAccountAndProfileCombo(getState()));
          setUserContext(currentProfile);
          recordUserIdOnce(Profile.selectors.id(currentProfile));
        }
      });
    };
  },

  acknowledgeBanner(key) {
    return {
      type: ActionTypes.ACKNOWLEDGE_ACCOUNT_BANNER,
      key,
    };
  },

  loadSubscriptions() {
    return dispatch => {
      Data.subscriptions().then(
        subscriptions => {
          dispatch({
            type: ActionTypes.LOAD_ACCOUNT_SUBSCRIPTIONS_DONE,
            subscriptions,
          });
        },
        error => {
          if (error.status === 401) {
            // invalid email & checksum, redirect to login page
            z.useful.goto('/app/login?next=' + window.location.pathname);
          } else {
            // use the legacy error handler for now
            z.app.errorHandler.showErrorMessage(error.status);
          }
        }
      );
    };
  },

  subscribe(channelKeys) {
    const patchData = _.fromPairs(
      channelKeys.map(channelKey => [channelKey, true])
    );
    return {
      type: ActionTypes.ADD_ACCOUNT_SUBSCRIPTIONS,
      channelKeys,
      promise: Data.patchSubscriptions(patchData),
    };
  },

  unsubscribe(channelKeys) {
    const patchData = _.fromPairs(
      channelKeys.map(channelKey => [channelKey, false])
    );
    return {
      type: ActionTypes.REMOVE_ACCOUNT_SUBSCRIPTIONS,
      channelKeys,
      promise: Data.patchSubscriptions(patchData),
    };
  },

  loadNotifications() {
    return dispatch => {
      Data.notifications().then(
        notifications => {
          dispatch({
            type: ActionTypes.LOAD_ACCOUNT_NOTIFICATIONS_DONE,
            notifications,
          });
        },
        error => {
          if (error.status === 401) {
            // invalid email & checksum, redirect to login page
            z.useful.goto('/app/login?next=' + window.location.pathname);
          } else {
            // use the legacy error handler for now
            z.app.errorHandler.showErrorMessage(error.status);
          }
        }
      );
    };
  },

  setSummarySchedule(value) {
    return {
      types: [
        ActionTypes.SET_ACCOUNT_SUMMARY_SCHEDULE,
        ActionTypes.SET_ACCOUNT_SUMMARY_SCHEDULE_DONE,
        ActionTypes.ADD_ACCOUNT_BANNER_NOTIFICATIONS_ERROR,
      ],
      value,
      promise: Data.patchNotifications({
        summary_schedule: value,
      }),
    };
  },

  setTriggerAlerts(value) {
    return {
      types: [
        ActionTypes.SET_ACCOUNT_TRIGGER_ALERTS,
        ActionTypes.SET_ACCOUNT_TRIGGER_ALERTS_DONE,
        ActionTypes.ADD_ACCOUNT_BANNER_NOTIFICATIONS_ERROR,
      ],
      value,
      promise: Data.patchNotifications({
        alert_triggers: value,
      }),
    };
  },

  setActionAlerts(value) {
    return {
      types: [
        ActionTypes.SET_ACCOUNT_ACTION_ALERTS,
        ActionTypes.SET_ACCOUNT_ACTION_ALERTS_DONE,
        ActionTypes.ADD_ACCOUNT_BANNER_NOTIFICATIONS_ERROR,
      ],
      value,
      promise: Data.patchNotifications({
        alert_actions: value,
      }),
    };
  },

  decodeMessage(payload) {
    return {
      type: ActionTypes.DECODE_MESSAGE,
      promise: Data.decodeMessage(payload),
    };
  },
};

export default actions;
